/**
 * The adapter: `module id → { route, icon }`.
 *
 * **This is the only thing this app contributes to its own navigation.** The
 * labels, the grouping, the ordering, the badges and the gating permission all
 * come from the server's manifest, which is already filtered by the enabled
 * services and by what this user holds.
 *
 * That split is the point. In the reference implementation the catalog was
 * declared once per client and drifted — labels, sections and even the gating
 * permission diverged between the web and mobile apps, so a tab appeared in one
 * and not the other for reasons nobody could reconstruct.
 *
 * **A module absent from this map is simply skipped**, which is what lets the
 * API ship a module before this app has built the screen: the catalog stays
 * whole and the sidebar shows only what exists here.
 */

import {
  Bot,
  BookOpen,
  MessagesSquare,
  PhoneCall,
  Building2,
  CircleUser,
  Database,
  Clock,
  FileCheck2,
  FileText,
  FolderKanban,
  Gauge,
  Hash,
  KeyRound,
  LayoutGrid,
  LifeBuoy,
  Mail,
  ChartColumn,
  HeartPulse,
  ListChecks,
  Receipt,
  ScrollText,
  ShieldAlert,
  Server,
  Settings,
  Share2,
  ShieldCheck,
  Target,
  Ticket,
  Users,
  Wallet,
  Landmark,
  Handshake,
  Percent,
  Scale,
  FileSignature,
  HandCoins,
  ShoppingCart,
  UserX,
  PlugZap,
  Webhook,
} from 'lucide-react';
import type { NavAdapter } from '@connect/web-shared';

const iconClass = 'size-4';

export const NAV_ADAPTER: NavAdapter = {
  // ── frame ─────────────────────────────────────────────────
  overview: { to: '/', icon: <Gauge className={iconClass} /> },

  // ── crm ───────────────────────────────────────────────────
  clients: { to: '/clients', icon: <Building2 className={iconClass} /> },
  contacts: { to: '/contacts', icon: <CircleUser className={iconClass} /> },
  'customer-database': { to: '/customers', icon: <Database className={iconClass} /> },
  proposals: { to: '/proposals', icon: <FileText className={iconClass} /> },

  // ── projects ──────────────────────────────────────────────
  projects: { to: '/projects', icon: <FolderKanban className={iconClass} /> },
  tasks: { to: '/tasks', icon: <ListChecks className={iconClass} /> },
  milestones: { to: '/milestones', icon: <Target className={iconClass} /> },
  allocations: { to: '/allocations', icon: <Share2 className={iconClass} /> },

  // ── support ───────────────────────────────────────────────
  tickets: { to: '/tickets', icon: <Ticket className={iconClass} /> },

  // ── people ────────────────────────────────────────────────
  employees: { to: '/employees', icon: <Users className={iconClass} /> },
  departments: { to: '/departments', icon: <LayoutGrid className={iconClass} /> },
  teams: { to: '/teams', icon: <Users className={iconClass} /> },
  'org-chart': { to: '/org-chart', icon: <Share2 className={iconClass} /> },

  // ── time ──────────────────────────────────────────────────
  'time-entries': { to: '/time-entries', icon: <Clock className={iconClass} /> },
  timesheets: { to: '/timesheets', icon: <Clock className={iconClass} /> },

  // ── finance ───────────────────────────────────────────────
  invoices: { to: '/invoices', icon: <Receipt className={iconClass} /> },
  payments: { to: '/payments', icon: <Wallet className={iconClass} /> },
  expenses: { to: '/expenses', icon: <Receipt className={iconClass} /> },

  // ── insight ───────────────────────────────────────────────
  reports: { to: '/reports', icon: <ScrollText className={iconClass} /> },
  'messaging-usage': { to: '/messaging/usage', icon: <ChartColumn className={iconClass} /> },
  'messaging-health': { to: '/messaging/health', icon: <HeartPulse className={iconClass} /> },
  // The accounts Connect holds at its vendors, and what is done at them.
  'messaging-providers': { to: '/messaging/providers', icon: <Server className={iconClass} /> },
  // Every client's wallet and credits, against the vendors' books.
  billing: { to: '/messaging/billing', icon: <Landmark className={iconClass} /> },
  // ── the partner programme (docs/30) ─────────────────────
  partners: { to: '/partners', icon: <Handshake className={iconClass} /> },
  'partner-registrations': { to: '/partners-customers', icon: <Users className={iconClass} /> },
  'partner-policies': { to: '/partners-policies', icon: <FileSignature className={iconClass} /> },
  'partner-ledger': { to: '/partners-commissions', icon: <Percent className={iconClass} /> },
  'partner-payouts': { to: '/partners-payouts', icon: <HandCoins className={iconClass} /> },
  'partner-tax': { to: '/partners-tax', icon: <Scale className={iconClass} /> },
  // Partners' bank and tax details, their proof, and the checks of them.
  'partner-checks': { to: '/partners-checks', icon: <ShieldCheck className={iconClass} /> },
  // SMS credit purchases waiting on Connect.
  'credit-purchases': { to: '/messaging/credit-purchases', icon: <ShoppingCart className={iconClass} /> },
  'ai-automation': { to: '/messaging/ai', icon: <Bot className={iconClass} /> },
  // Every client's webhooks: the platform's settings, the delivery log, usage.
  webhooks: { to: '/messaging/webhooks', icon: <Webhook className={iconClass} /> },
  // The applications clients may connect: registered, approved, suspended.
  'connected-apps': { to: '/messaging/connected-apps', icon: <PlugZap className={iconClass} /> },
  'rcs-pricing': { to: '/messaging/rcs', icon: <MessagesSquare className={iconClass} /> },
  calling: { to: '/messaging/calling', icon: <PhoneCall className={iconClass} /> },
  // In the manifest only on an installation that offers email.
  'email-pricing': { to: '/messaging/email', icon: <Mail className={iconClass} /> },

  // ── access ────────────────────────────────────────────────
  users: { to: '/access/users', icon: <CircleUser className={iconClass} /> },
  roles: { to: '/access/roles', icon: <ShieldCheck className={iconClass} /> },
  'access-control': { to: '/access/control', icon: <KeyRound className={iconClass} /> },
  'audit-log': { to: '/access/audit', icon: <ScrollText className={iconClass} /> },

  // ── legal ─────────────────────────────────────────────────
  'legal-documents': { to: '/legal/documents', icon: <FileCheck2 className={iconClass} /> },
  'consent-requirements': { to: '/legal/requirements', icon: <ListChecks className={iconClass} /> },
  'consent-records': { to: '/legal/consents', icon: <ScrollText className={iconClass} /> },
  'security-incidents': { to: '/legal/incidents', icon: <ShieldAlert className={iconClass} /> },
  'individual-requests': { to: '/individual-requests', icon: <UserX className={iconClass} /> },

  // ── admin ─────────────────────────────────────────────────
  'service-switch': { to: '/settings/services', icon: <LifeBuoy className={iconClass} /> },
  'record-codes': { to: '/settings/record-codes', icon: <Hash className={iconClass} /> },
  docs: { to: '/docs', icon: <BookOpen className={iconClass} /> },
  settings: { to: '/settings', icon: <Settings className={iconClass} /> },
};
